import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Instant Quote search/create CRUD page in a tab.
 */
const INSTANT_QUOTES_SEARCH_CRUD_ROUTE =
  "instant-quotes/instant-quotesConfigCrud";

//endregion

//region Component

/**
 * Renders the Instant Quotes search configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in tabs mode;
 * - keep title and subtitle visually compact through config-page.css;
 * - render the Instant Quotes search MTable;
 * - expose Create and View workflows;
 * - hide Edit/Copy/Print actions;
 * - persist operation and selected row before opening the detail tab.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Instant Quotes search configuration tab.
 */
export default function InstantQuotesSearchConfig({
  roles,
  onCreateTab,
  toastMessages,
}) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [refresh, setRefresh] = useState(false);

  /**
   * Removes table actions that are not required in this workflow.
   *
   * Business rule:
   * - Create and View are supported;
   * - Edit/Copy/Print are intentionally hidden;
   * - Delete remains available only if MTable permissions allow it.
   */
  const removeContextItems = useMemo(
    () => ["Update", "Print", "Copy"],
    []
  );

  /**
   * Opens the Instant Quote search/create CRUD tab.
   *
   * Flow:
   * - persists the operation in M_State;
   * - validates row selection for View;
   * - persists the selected search row when required;
   * - opens the configured route with a localized tab label.
   *
   * @param {"Create"|"View"} operation - Requested operation.
   * @param {object|null|undefined} row - Selected rate query row.
   * @returns {void}
   */
  const openRateQueryCrud = useCallback(
    (operation, row) => {
      dispatch(
        M_StateActions.addM_State({
          key: "operation",
          value: operation,
        })
      );

      if (operation === "View") {
        if (!row) {
          toastMessages("error", "", t("SelectedLine"));
          return;
        }

        dispatch(
          M_StateActions.addM_State({
            key: "InstantQuoteSearchLine",
            value: row,
          })
        );
      }

      const tabLabel =
        operation === "Create"
          ? t("LabelNewInstantQuote")
          : t("LabelViewInstantQuote", {
            name: row?.Name ?? "",
          });

      onCreateTab(INSTANT_QUOTES_SEARCH_CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  /**
   * Replaces MTable default Create/View behavior with the Instant Quotes workflow.
   */
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ["View", (row) => openRateQueryCrud("View", row)],
        ["Create", () => openRateQueryCrud("Create")],
      ]),
    [openRateQueryCrud]
  );

  return (
    <div
      className="config-page config-page--tabs"
      style={{ "--config-page-title-color": ctx.secondaryColor }}
    >
      <div className="config-page__header config-page__header--tabs">
        <div className="config-page__title config-page__title--tabs">
          {t("InstantQuotesSearch")}
        </div>

        <div className="config-page__description config-page__description--tabs">
          {t("InfoInstantQuotesSearch")}
        </div>
      </div>

      <div className="config-page__table">
        <MTable
          filtersOnTable
          entityName="CargofiveRateQueries"
          roleName="InstantQuotes"
          rolePermissions={roles?.map((role) => role.Value)}
          removeContextItems={removeContextItems}
          refresh={refresh}
          setRefresh={setRefresh}
          useSessionStorage={false}
          replaceFunctions={replaceFunctions}
          tabsRailWidth={250}
        />
      </div>
    </div>
  );
}

//endregion

//region PropTypes

InstantQuotesSearchConfig.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};

//endregion